/**
 * <ry-tag-input>
 *
 * Free-form tag entry with delimiter splitting and paste support.
 *
 * Usage:
 * <ry-tag-input placeholder="Add tags..." delimiter="," max-tags="5" name="tags" value="js,ts,css"></ry-tag-input>
 *
 * Events: ry:change, ry:add, ry:remove
 */

import { RyElement } from '../core/ry-element.js';

export class RyTagInput extends RyElement {
  #tags: string[] = [];

  setup(): void {
    this.#transformMarkup();
    this.#parseInitialValue();
  }

  #transformMarkup(): void {
    if (this.$('[data-ry-target="container"]')) return;

    const placeholder = this.getAttribute('placeholder') ?? 'Add tag...';
    const name = this.getAttribute('name') ?? '';
    const disabled = this.hasAttribute('disabled');

    this.innerHTML = `
      <div data-ry-target="container" class="ry-tag-input__container">
        <div data-ry-target="tags" class="ry-tag-input__tags"></div>
        <input data-ry-target="input" class="ry-tag-input__input" type="text" placeholder="${placeholder}" ${disabled ? 'disabled' : ''}>
      </div>
      <input data-ry-target="hidden" type="hidden" ${name ? `name="${name}"` : ''}>
    `;

    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input) {
      this.on(input, 'keydown', this.#handleKeydown as EventListener);
      this.on(input, 'paste', this.#handlePaste as EventListener);
    }

    const container = this.$('[data-ry-target="container"]');
    if (container) {
      this.on(container, 'click', () => input?.focus());
    }

    this.on(this, 'ry:remove', this.#handleTagRemove as EventListener);
  }

  #parseInitialValue(): void {
    const value = this.getAttribute('value');
    if (!value) return;

    const delimiter = this.getAttribute('delimiter') ?? ',';
    const parts = value.split(delimiter).map(s => s.trim()).filter(Boolean);
    for (const tag of parts) {
      this.#addTag(tag);
    }
  }

  #handleKeydown = (e: KeyboardEvent): void => {
    const input = e.target as HTMLInputElement;
    const delimiter = this.getAttribute('delimiter') ?? ',';
    const val = input.value.trim();

    if (e.key === 'Enter' || e.key === delimiter || e.key === 'Tab') {
      if (val) {
        e.preventDefault();
        this.#addTag(val);
        input.value = '';
      } else if (e.key === 'Tab') {
        return; // allow natural tab
      }
    }

    if (e.key === 'Backspace' && !input.value && this.#tags.length > 0) {
      this.#removeLastTag();
    }
  };

  #handlePaste = (e: ClipboardEvent): void => {
    e.preventDefault();
    const text = e.clipboardData?.getData('text') ?? '';
    const delimiter = this.getAttribute('delimiter') ?? ',';
    const parts = text.split(new RegExp(`[${delimiter}\\t\\n]+`)).map(s => s.trim()).filter(Boolean);
    for (const tag of parts) {
      this.#addTag(tag);
    }
    const input = this.$<HTMLInputElement>('[data-ry-target="input"]');
    if (input) input.value = '';
  };

  #handleTagRemove = (e: CustomEvent): void => {
    const value = (e.detail as { value: string }).value;
    const idx = this.#tags.indexOf(value);
    if (idx >= 0) {
      this.#tags.splice(idx, 1);
      this.#syncHidden();
      this.emit('change', { values: [...this.#tags] });
    }
  };

  #addTag(text: string): void {
    const maxTags = this.getAttribute('max-tags');
    if (maxTags && this.#tags.length >= parseInt(maxTags, 10)) return;

    if (!this.hasAttribute('allow-duplicates') && this.#tags.includes(text)) return;

    this.#tags.push(text);

    const tagsContainer = this.$('[data-ry-target="tags"]');
    if (tagsContainer) {
      const tag = document.createElement('ry-tag');
      tag.setAttribute('removable', '');
      tag.setAttribute('size', 'sm');
      tag.setAttribute('data-value', text);
      tag.textContent = text;
      tagsContainer.appendChild(tag);
    }

    this.#syncHidden();
    this.emit('add', { value: text });
    this.emit('change', { values: [...this.#tags] });
  }

  #removeLastTag(): void {
    const tagsContainer = this.$('[data-ry-target="tags"]');
    const lastTag = tagsContainer?.lastElementChild;
    if (lastTag) {
      const val = (lastTag as HTMLElement).dataset.value ?? '';
      lastTag.remove();
      this.#tags.pop();
      this.#syncHidden();
      this.emit('remove', { value: val, label: val });
      this.emit('change', { values: [...this.#tags] });
    }
  }

  #syncHidden(): void {
    const hidden = this.$<HTMLInputElement>('[data-ry-target="hidden"]');
    const delimiter = this.getAttribute('delimiter') ?? ',';
    if (hidden) {
      hidden.value = this.#tags.join(delimiter);
    }
  }

  get value(): string {
    const delimiter = this.getAttribute('delimiter') ?? ',';
    return this.#tags.join(delimiter);
  }

  get values(): string[] {
    return [...this.#tags];
  }
}

customElements.define('ry-tag-input', RyTagInput);
